/**
 * citationTargetGate — PURE structural integrity check for a report's
 * in-body citations against (a) the rendered section list and (b) the
 * reference list.
 *
 * ─── Why ──────────────────────────────────────────────────────────────
 *
 * Report-gen quality remediation (tidy-sniffing-teacup rev9). Two failure
 * modes break a report's citation contract WITHOUT being numeric or
 * semantic — they are purely structural, so the existing tier-2e number
 * reconcilers and the LLM-backed support verifiers never catch them:
 *
 *   (1) DEAD POINTER — a citation says "Cited in §Grid Study" (or carries
 *       a target sectionId), but that section was DROPPED from the render
 *       (omitted by the type projection, retired, or never produced by the
 *       divergent SiteRagReportView / SiteRagPrintExport trees). The
 *       superscript / back-ref strip points the reader at a section that
 *       isn't on the page — a broken pointer.
 *
 *   (2) ORPHAN REFERENCE — a `references[]` entry that is never cited
 *       anywhere in the body. The appendix renders a numbered source the
 *       narrative never leans on; it inflates the apparent evidence base
 *       and (worse) shifts the 1-based `[Ref N]` numbering off-by-one risk
 *       for everything after it.
 *
 * This gate is the STRUCTURAL twin of `siteReportAuditor`'s tier-2f
 * appendix-bijection check, but it operates on the *resolved* render
 * (the section-id list `computeExpectedSectionIds` produces, and the
 * structured back-ref map `buildCitedInMap` produces) rather than
 * re-scraping appendix prose with a regex. That makes it deterministic,
 * unit-testable without a DOM, and free of the markdown-shape assumptions
 * the tier-2f text walk has to make.
 *
 * ─── Discipline ───────────────────────────────────────────────────────
 *
 *  • PURE. No I/O, no store, no DOM, never mutates inputs, never throws on
 *    bad data — malformed entries are surfaced as violations, not crashes.
 *  • Self-contained. The only cross-module dependency is the
 *    `RenderedSection` shape, which the caller fills from
 *    `computeExpectedSectionIds` (reportSectionManifest.ts) /
 *    `report.sectionOrder`. The checker does NOT import the report type —
 *    P1/P2 adapt their data into the flat inputs below.
 *  • Fail-closed for dead pointers (an unresolvable §-target is a
 *    violation), fail-open for empty inputs (no citations + no refs ⇒
 *    `clean`, never a false positive).
 *
 * ─── Wiring (deferred to P1/P2) ───────────────────────────────────────
 *
 *  For now this file ONLY exports the checker + its result types. The
 *  block-vs-warn policy and the adapter from `SiteRagReport` →
 *  `CitationTargetGateInput` are P1/P2's job (see the trailing note in the
 *  task hand-off). `dead-pointer` is intended to be BLOCKING (a reader-
 *  facing broken pointer); `orphan-reference` is intended to be WARN
 *  (a quality signal, mirroring tier-2f's orphan severity).
 */

/* ── Inputs ──────────────────────────────────────────────────────────── */

/**
 * One rendered section, as it actually appears on the page. The caller
 * builds this list from `computeExpectedSectionIds(reportType,
 * report.sectionOrder, template)` (canonical id list) and, where
 * available, the section's human label (the `defaultLabel` the
 * `Cited in §X` strip prints).
 *
 *  - `id`    — canonical section id (e.g. `'gridStudy'`). REQUIRED; this
 *              is the primary match key for an id-style citation target.
 *  - `label` — the display heading shown in `§<label>` (e.g.
 *              `'Grid Study'`). OPTIONAL; when present it lets a
 *              label-style `Cited in §X` target resolve even if the gate
 *              caller only had the printed heading, not the id.
 */
export interface RenderedSection {
  id: string;
  label?: string;
  /**
   * FIELD names whose content renders inside this section (2026-08-05).
   *
   * Citations address content by REPORT FIELD (`btmOptions`,
   * `developmentRisks`, `recommendations`, `climateSevereWeather`), while
   * rendered sections are addressed by SECTION ID (`btmOverview`,
   * `riskAssessment`, `actionPlan`). Without aliases the two namespaces never
   * meet and every such citation is reported as a dead pointer — 6
   * block-severity false positives on every feasibility report in the live
   * v1.22.1 run, each one pointing at a section that WAS present and
   * populated. Aliases let a field-addressed citation resolve to the section
   * that renders it.
   */
  aliases?: readonly string[];
}

/**
 * One in-body citation target — "this citation claims to live in §<section>".
 *
 * A report surfaces these in two equivalent shapes the caller flattens
 * into this one:
 *   - the `Cited in §X, §Y` back-ref strip (`buildCitedInMap`): one
 *     target per (refIndex, section-label) pair; and/or
 *   - a structured claim carrying a `sectionId` (`ReportClaim.sectionId`)
 *     plus a `ref:N` source: one target per (refIndex, sectionId) pair.
 *
 *  - `section`     — the section this citation points AT, as either a
 *                    canonical id OR the printed `§<label>` heading. A
 *                    leading `§` and surrounding whitespace are tolerated
 *                    and stripped during resolution.
 *  - `refIndex`    — OPTIONAL 1-based index into the reference list this
 *                    citation cites. When present it (a) marks the matching
 *                    reference as "cited" for the orphan check and (b)
 *                    enriches the dead-pointer violation message.
 *  - `label`       — OPTIONAL human-readable origin tag for diagnostics
 *                    (e.g. the claim text or the `[Ref N]` token). Never
 *                    used for matching; surfaced verbatim in the violation.
 */
export interface CitationTarget {
  section: string;
  refIndex?: number;
  label?: string;
}

/**
 * One reference-list entry (1-based ordinal = position in the array + 1).
 * Kept structurally minimal so the caller can pass `report.references`
 * (DataSource[]) or any subset without coupling this module to the report
 * type. A reference is an ORPHAN when no `CitationTarget.refIndex` points
 * at its ordinal AND its `citedInThisReport` flag is not explicitly true.
 *
 *  - `source`            — human-readable source name, for diagnostics.
 *  - `field`             — the report field the row backs, for diagnostics.
 *  - `citedInThisReport` — the post-LLM citation-pass flag (DataSource).
 *                          When explicitly `true` the reference is treated
 *                          as cited even if no structured target referenced
 *                          its ordinal (back-compat: the body may cite it
 *                          via a style this gate's caller didn't flatten).
 *                          `false`/`undefined` ⇒ rely on `refIndex` targets.
 */
export interface ReferenceEntry {
  source?: string;
  field?: string;
  citedInThisReport?: boolean;
}

export interface CitationTargetGateInput {
  /** In-body citation targets (flattened back-refs + structured claims). */
  citations: readonly CitationTarget[];
  /** The reference list, in 1-based ordinal order (array index + 1). */
  references: readonly ReferenceEntry[];
  /** The sections actually rendered, in order. */
  renderedSections: readonly RenderedSection[];
}

/* ── Output ──────────────────────────────────────────────────────────── */

export type CitationViolationKind = 'dead-pointer' | 'orphan-reference';

export interface CitationTargetViolation {
  kind: CitationViolationKind;
  severity: 'block' | 'warn';
  /** Human-readable, single-line explanation. */
  message: string;
  /**
   * For `dead-pointer`: the unresolved section reference (verbatim, as the
   * citation carried it). For `orphan-reference`: the reference's source
   * name (or its ordinal label when no source string is present).
   */
  target: string;
  /** 1-based reference index, when the violation is tied to one. */
  refIndex?: number;
  /** Origin tag passed through from the citation, for drill-down. */
  context?: string;
}

export interface CitationTargetGateResult {
  /** True when there are NO violations of any kind. */
  clean: boolean;
  violations: CitationTargetViolation[];
  /** Convenience: violations whose severity is `block`. */
  blocking: CitationTargetViolation[];
  stats: {
    citations: number;
    references: number;
    renderedSections: number;
    deadPointers: number;
    orphanReferences: number;
  };
}

/* ── Resolution helpers ──────────────────────────────────────────────── */

/**
 * Normalise a section reference for comparison: drop a leading `§`,
 * collapse internal whitespace, lower-case. So `'§Grid Study'`,
 * `'grid study'`, and `'  Grid   Study '` all compare equal — and an
 * id-style `'gridStudy'` is matched against ids separately (below).
 */
function normalizeSectionRef(raw: string): string {
  return raw
    .replace(/^\s*§\s*/, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/**
 * Format-insensitive form: alphanumerics only, so `climateSevereWeather`,
 * "Climate Severe Weather" and `climate_severe_weather` all collapse to one
 * key (2026-08-05). This gate HARD-FAILS a report on a single dead pointer,
 * so a naming-format difference between the producer of a citation and the
 * producer of a section id must never manufacture a block — that mismatch
 * already cost 6 false blocks on every live feasibility report.
 */
function collapseSectionRef(raw: string): string {
  return raw.replace(/^\s*§\s*/, '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Build the lookup set of every form a rendered section can be addressed
 * by: its canonical id (exact, case-preserving id match is the common
 * structured-claim path) plus normalized id and normalized label (the
 * `§<label>` back-ref path).
 */
function buildRenderedKeys(sections: readonly RenderedSection[]): {
  ids: Set<string>;
  normalized: Set<string>;
  collapsed: Set<string>;
} {
  const ids = new Set<string>();
  const normalized = new Set<string>();
  const collapsed = new Set<string>();
  for (const s of sections) {
    if (typeof s.id === 'string' && s.id.length > 0) {
      ids.add(s.id);
      normalized.add(normalizeSectionRef(s.id));
      collapsed.add(collapseSectionRef(s.id));
    }
    if (typeof s.label === 'string' && s.label.length > 0) {
      normalized.add(normalizeSectionRef(s.label));
      collapsed.add(collapseSectionRef(s.label));
    }
    for (const alias of s.aliases ?? []) {
      if (typeof alias === 'string' && alias.length > 0) {
        ids.add(alias);
        normalized.add(normalizeSectionRef(alias));
        collapsed.add(collapseSectionRef(alias));
      }
    }
  }
  return { ids, normalized, collapsed };
}

/**
 * True when a citation's `section` resolves to one of the rendered
 * sections — by exact id, by normalized id, or by normalized label.
 */
function sectionResolves(
  section: string,
  keys: { ids: Set<string>; normalized: Set<string>; collapsed: Set<string> },
): boolean {
  if (keys.ids.has(section)) return true;
  if (keys.normalized.has(normalizeSectionRef(section))) return true;
  // Format-insensitive last resort — see collapseSectionRef.
  if (keys.collapsed.has(collapseSectionRef(section))) return true;
  const rootSection = section.replace(/^\s*§\s*/, '').split(/[.[]/)[0]?.trim() ?? '';
  if (rootSection.length > 0 && rootSection !== section) {
    if (keys.ids.has(rootSection)) return true;
    if (keys.normalized.has(normalizeSectionRef(rootSection))) return true;
    if (keys.collapsed.has(collapseSectionRef(rootSection))) return true;
  }
  return false;
}

/* ── The gate ────────────────────────────────────────────────────────── */

/**
 * Check a report's citation targets + reference list against the rendered
 * section list. PURE: returns the violation set, never throws, never
 * mutates inputs.
 *
 * Dead pointers (BLOCK): every citation whose `section` does not resolve
 * into `renderedSections` is reported once. A citation with a blank/
 * whitespace-only `section` is itself a dead pointer (it points nowhere).
 *
 * Orphan references (WARN): every reference ordinal that no citation's
 * `refIndex` points at AND whose `citedInThisReport` is not `true` is
 * reported once. References are 1-based: `references[0]` is ordinal 1.
 *
 * Out-of-range `refIndex` values (≤0, non-integer, or past the end of the
 * reference list) do NOT crash and do NOT mark any reference cited; they
 * simply fail to satisfy any orphan — and, because such an index can never
 * back a real source, the dead-pointer message notes it when the same
 * citation's section is also unresolved.
 */
export function checkCitationTargets(
  input: CitationTargetGateInput,
): CitationTargetGateResult {
  const { citations, references, renderedSections } = input;
  const keys = buildRenderedKeys(renderedSections);

  const violations: CitationTargetViolation[] = [];

  // ── (1) Dead pointers ──────────────────────────────────────────────
  // Dedupe by the verbatim section ref so a section cited 12 times that
  // got dropped surfaces ONE violation, not twelve.
  const seenDeadSection = new Set<string>();
  for (const c of citations) {
    const sectionRef = typeof c.section === 'string' ? c.section : '';
    const trimmed = sectionRef.trim();
    if (trimmed.length > 0 && sectionResolves(trimmed, keys)) continue;

    const dedupeKey = trimmed.length > 0 ? trimmed.toLowerCase() : `__blank__`;
    if (seenDeadSection.has(dedupeKey)) continue;
    seenDeadSection.add(dedupeKey);

    const where = trimmed.length > 0 ? `§${trimmed.replace(/^\s*§\s*/, '')}` : '(blank section)';
    const refNote = typeof c.refIndex === 'number' ? ` (cited by [Ref ${c.refIndex}])` : '';
    violations.push({
      kind: 'dead-pointer',
      severity: 'block',
      message: `Citation points to ${where}${refNote}, which is not in the rendered section list.`,
      target: trimmed.length > 0 ? trimmed : '(blank)',
      ...(typeof c.refIndex === 'number' ? { refIndex: c.refIndex } : {}),
      ...(c.label ? { context: c.label } : {}),
    });
  }

  // ── (2) Orphan references ──────────────────────────────────────────
  // A reference ordinal is "cited" when some citation's refIndex points at
  // it. The DataSource.citedInThisReport flag is honoured as an additional
  // cited signal (a body citation style the caller didn't flatten).
  const citedOrdinals = new Set<number>();
  for (const c of citations) {
    if (
      typeof c.refIndex === 'number' &&
      Number.isInteger(c.refIndex) &&
      c.refIndex >= 1 &&
      c.refIndex <= references.length
    ) {
      citedOrdinals.add(c.refIndex);
    }
  }

  for (let i = 0; i < references.length; i++) {
    const ordinal = i + 1;
    const ref = references[i]!;
    if (citedOrdinals.has(ordinal)) continue;
    if (ref.citedInThisReport === true) continue;
    // ORPHAN-SCOPE-1 (2026-07-03) — dataset-tier references carry a `field`
    // (the SED provider slot they were auto-attached from: airQuality,
    // seismic, nuclear, …). They are CONSULTED-BY-DESIGN bibliography
    // entries — the References section lists them cited or not — so an
    // uncited one is not an orphan. Live Redhead regen: 24 of 50 grounding
    // violations were exactly these (refs 13-36, the provider datasets).
    // Uploaded-document / research references (no `field`) remain flagged:
    // an uncited uploaded doc IS signal the gen ignored user evidence.
    if (typeof ref.field === 'string' && ref.field.trim().length > 0) continue;

    const sourceName =
      typeof ref.source === 'string' && ref.source.trim().length > 0
        ? ref.source.trim()
        : `reference ${ordinal}`;
    const fieldNote =
      typeof ref.field === 'string' && ref.field.trim().length > 0
        ? ` (field: ${ref.field.trim()})`
        : '';
    violations.push({
      kind: 'orphan-reference',
      severity: 'warn',
      message: `[Ref ${ordinal}] "${sourceName}"${fieldNote} is in the reference list but never cited in the body.`,
      target: sourceName,
      refIndex: ordinal,
    });
  }

  const deadPointers = violations.filter((v) => v.kind === 'dead-pointer').length;
  const orphanReferences = violations.filter((v) => v.kind === 'orphan-reference').length;
  const blocking = violations.filter((v) => v.severity === 'block');

  return {
    clean: violations.length === 0,
    violations,
    blocking,
    stats: {
      citations: citations.length,
      references: references.length,
      renderedSections: renderedSections.length,
      deadPointers,
      orphanReferences,
    },
  };
}
